// Хүргэлт удирдлага hub (canvas Ord_DeliveryHub / TabsA / TabsB): 7 sub-tabs
// Хүлээлтэнд / Маршрут / Хүлээлцэл / Идэвхтэй / Амжилтгүй / Хүргэсэн / Бэлэн төлөлт.
// Each tab is gated by the permission its list endpoint checks (BE contract §1), so a
// visible tab never answers 403.

import { Badge, Box, Tabs, Text, Title } from "@mantine/core"
import { PlusIcon } from "@phosphor-icons/react"
import { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router"
import useSWR from "swr"
import { DeliveryApi } from "../../apis"
import { ActiveDeliveries } from "../../components/active-deliveries"
import { CustodyTransfers } from "../../components/custody-transfers"
import { DeliveryActionButton } from "../../components/delivery/action-controls"
import { useDeliveryActions } from "../../components/delivery/delivery-actions"
import { DeliveryRoutes } from "../../components/delivery-routes"
import { FailedDeliveries } from "../../components/failed-deliveries"
import { PageLayout } from "../../components/layout"
import { PendingDeliveries } from "../../components/pending-deliveries"
import { useDeliveryStats } from "../../components/pending-deliveries/hooks"
import { IPermissionCode, usePermission } from "../../components/permission"
import { CodReconciliationWorklist } from "../../components/settings/CodReconciliationWorklist"
import { SupplierDeliveredList } from "../../components/supplier-delivered/list"
import { DeniedCard } from "../../components/ui"
import { message } from "../../utils/message"

type TabKey = "PENDING" | "ROUTES" | "HANDOVER" | "ACTIVE" | "FAILED" | "CONFIRM" | "COD"

const TAB_ORDER: TabKey[] = ["PENDING", "ROUTES", "HANDOVER", "ACTIVE", "FAILED", "CONFIRM", "COD"]

const TAB_LABEL: Record<TabKey, string> = {
  PENDING: "delivery.tabs.pending",
  ROUTES: "delivery.tabs.routes",
  HANDOVER: "delivery.tabs.handover",
  ACTIVE: "delivery.tabs.active",
  FAILED: "delivery.tabs.failed",
  CONFIRM: "delivery.tabs.delivered",
  COD: "delivery.tabs.cod",
}

/** Tabs that render a section heading (title + one-line description) above their content. */
const SECTION_KEY: Partial<Record<TabKey, string>> = {
  ACTIVE: "active",
  FAILED: "failed",
  CONFIRM: "delivered",
  COD: "cod",
}

const DEFAULT_TAB: TabKey = "PENDING"

function TabCount({ count, active }: Readonly<{ count: number | undefined; active: boolean }>) {
  if (count == null) return null
  return (
    <Badge size="md" radius="xl" color={active ? "dark" : "gray"} variant={active ? "filled" : "light"} ff="monospace">
      {count}
    </Badge>
  )
}

function TabSection({ title, description }: Readonly<{ title: string; description: string }>) {
  return (
    <Box mb="md">
      <Title order={3} fz="var(--nw-fz-drawer)">
        {title}
      </Title>
      <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
        {description}
      </Text>
    </Box>
  )
}

/** Gate = the permission the tab's list endpoint checks (BE contract §1); several = any of. */
function useTabGate(): Record<TabKey, { ok: boolean; code: IPermissionCode }> {
  const draft = usePermission("ORD_DN_DRAFT_LIST").hasAccess
  const assigned = usePermission("ORD_DN_ASSIGNED_LIST").hasAccess
  const delivered = usePermission("ORD_DN_DELIVERED_LIST").hasAccess
  const mng = usePermission("ORD_DN_MNG").hasAccess
  const plan = usePermission("DEL_ROUTE_PLAN").hasAccess
  return {
    PENDING: { ok: draft, code: "ORD_DN_DRAFT_LIST" },
    // delivery-route/list.ts checks ORD_DN_MNG | DEL_ROUTE_PLAN.
    ROUTES: { ok: mng || plan, code: "DEL_ROUTE_PLAN" },
    HANDOVER: { ok: assigned, code: "ORD_DN_ASSIGNED_LIST" },
    ACTIVE: { ok: assigned, code: "ORD_DN_ASSIGNED_LIST" },
    // failed-global-list.ts and cod-reconciliation/list.ts check ORD_DN_MNG (O102, O113).
    FAILED: { ok: mng, code: "ORD_DN_MNG" },
    CONFIRM: { ok: delivered, code: "ORD_DN_DELIVERED_LIST" },
    COD: { ok: mng, code: "ORD_DN_MNG" },
  }
}

export function SupplierDeliveryTabs() {
  const { t } = useTranslation(["delivery", "common"])
  const navigate = useNavigate()
  const location = useLocation()
  const { permissionState } = useDeliveryActions()
  const gate = useTabGate()

  const tabFromUrl = (): TabKey => {
    const value = new URLSearchParams(location.search).get("tab") as TabKey | null
    return value && TAB_ORDER.includes(value) ? value : DEFAULT_TAB
  }

  const [activeTab, setActiveTab] = useState<TabKey>(tabFromUrl())
  const deliveredListRef = useRef<{ reload?: () => void } | null>(null)
  const { stats } = useDeliveryStats()
  // O108: the Идэвхтэй badge counts the same 4 statuses its list shows (incl. PENDING_HANDOVER).
  const { data: activeStats } = useSWR(gate.ACTIVE.ok ? ["swr.delivery.active-stats", null] : null, () =>
    DeliveryApi.activeStats({}),
  )

  useEffect(() => {
    const next = tabFromUrl()
    if (next !== activeTab) setActiveTab(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  // O99: the approve page returns here with ?success=true after the last warehouse is confirmed.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get("success") === "true") {
      message.success(t("manage.hub.approveSuccess"))
      params.delete("success")
      navigate(`/supplier-delivery?${params.toString()}`, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search])

  const onTabChange = (value: string | null) => {
    const next = (value as TabKey) ?? DEFAULT_TAB
    setActiveTab(next)
    const params = new URLSearchParams(location.search)
    params.set("tab", next)
    navigate(`/supplier-delivery?${params.toString()}`, { replace: true })
    if (next === "CONFIRM") {
      setTimeout(() => deliveredListRef.current?.reload?.(), 200)
    }
  }

  const counts: Record<TabKey, number | undefined> = {
    PENDING: stats?.tabs.pending,
    ROUTES: stats?.tabs.routes,
    HANDOVER: stats?.tabs.handover,
    ACTIVE: activeStats?.active ?? stats?.tabs.active,
    FAILED: stats?.tabs.failed,
    CONFIRM: stats?.tabs.delivered,
    COD: stats?.tabs.cod,
  }

  const panel = (tab: TabKey) => {
    switch (tab) {
      case "PENDING":
        return <PendingDeliveries />
      case "ROUTES":
        return <DeliveryRoutes />
      case "HANDOVER":
        return <CustodyTransfers />
      case "ACTIVE":
        return <ActiveDeliveries />
      case "FAILED":
        return <FailedDeliveries />
      case "CONFIRM":
        return <SupplierDeliveredList ref={deliveredListRef} />
      case "COD":
        return <CodReconciliationWorklist />
    }
  }

  return (
    <PageLayout
      title={t("delivery.title")}
      subTitle={activeTab === "PENDING" ? t("manage.section.pending.description") : t("delivery.subtitle")}
      breadcrumb={[{ to: "/", label: t("common:breadcrumb.home") }, { label: t("delivery.breadcrumb") }]}
      extra={[
        <DeliveryActionButton
          key="new-route"
          state={permissionState("createRoute")}
          variant="default"
          leftSection={<PlusIcon size={16} />}
          onClick={() => navigate("/delivery-routes/new")}
        >
          {t("manage.hub.newRoute")}
        </DeliveryActionButton>,
      ]}
    >
      <Tabs value={activeTab} onChange={onTabChange} keepMounted={false}>
        <Tabs.List>
          {TAB_ORDER.map((tab) => (
            <Tabs.Tab key={tab} value={tab} rightSection={<TabCount count={counts[tab]} active={tab === activeTab} />}>
              {t(TAB_LABEL[tab])}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {TAB_ORDER.map((tab) => {
          const section = SECTION_KEY[tab]
          return (
            <Tabs.Panel key={tab} value={tab} mt="lg">
              {gate[tab].ok ? (
                <>
                  {section && (
                    <TabSection
                      title={t(`manage.section.${section}.title`)}
                      description={t(`manage.section.${section}.description`)}
                    />
                  )}
                  {panel(tab)}
                </>
              ) : (
                <DeniedCard permission={gate[tab].code} title={t("manage.hub.deniedTitle")} />
              )}
            </Tabs.Panel>
          )
        })}
      </Tabs>
    </PageLayout>
  )
}
